import React, { useState, useRef, useEffect } from "react";

/**
 * MiniOrbWidget — a draggable floating mini version of the main orb.
 *
 * Owner asked: "can I have the orb as a pop up little widget I can move
 * around that behaves the same way as the big un-moveable one, always at
 * the front layer?"
 *
 * - Sits at z-index 95 (above everything except the mirror-open prompt at 9999).
 * - Tap → fires the same `rawco-orb-tap` event the big orb fires, so the
 *   wake / TTS unlock / dashboard listen behaviour is identical.
 * - Drag the orb body to move; position persists in localStorage.
 * - Tap the ─ pip to shrink to a tiny pulse-dot; tap the dot to expand.
 * - Reflects orb activity state (idle / listening / speaking) via props.
 */
export default function MiniOrbWidget({ active = false, speaking = false, orbColor }) {
  const [collapsed, setCollapsed] = useState(
    () => localStorage.getItem("rawco_mini_orb_collapsed") === "1"
  );
  const [pos, setPos] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem("rawco_mini_orb_pos") || "null");
      if (saved && typeof saved.x === "number") return saved;
    } catch {}
    return { x: window.innerWidth - 140, y: 120 }; // default: top-right
  });
  const dragRef = useRef(null);
  const movedRef = useRef(false);

  useEffect(() => {
    localStorage.setItem("rawco_mini_orb_collapsed", collapsed ? "1" : "0");
  }, [collapsed]);

  useEffect(() => {
    const onResize = () => setPos((p) => ({
      x: Math.max(0, Math.min(p.x, window.innerWidth - 60)),
      y: Math.max(0, Math.min(p.y, window.innerHeight - 60)),
    }));
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const startDrag = (e) => {
    const evt = e.touches ? e.touches[0] : e;
    dragRef.current = { startX: evt.clientX, startY: evt.clientY, baseX: pos.x, baseY: pos.y };
    movedRef.current = false;
    const move = (ev) => {
      const m = ev.touches ? ev.touches[0] : ev;
      const dx = m.clientX - dragRef.current.startX;
      const dy = m.clientY - dragRef.current.startY;
      if (Math.abs(dx) + Math.abs(dy) > 4) movedRef.current = true;
      setPos({
        x: Math.max(0, Math.min(dragRef.current.baseX + dx, window.innerWidth - 60)),
        y: Math.max(0, Math.min(dragRef.current.baseY + dy, window.innerHeight - 60)),
      });
    };
    const stop = () => {
      setPos((p) => { localStorage.setItem("rawco_mini_orb_pos", JSON.stringify(p)); return p; });
      window.removeEventListener("mousemove", move);
      window.removeEventListener("touchmove", move);
      window.removeEventListener("mouseup", stop);
      window.removeEventListener("touchend", stop);
    };
    window.addEventListener("mousemove", move);
    window.addEventListener("touchmove", move, { passive: false });
    window.addEventListener("mouseup", stop);
    window.addEventListener("touchend", stop);
  };

  // Tap (no drag) = fires the same event the big orb fires
  const handleTap = () => {
    if (movedRef.current) return; // ignore taps that were drags
    window.dispatchEvent(new CustomEvent("rawco-orb-tap"));
  };

  const colour = orbColor || "#ffb800";

  // Collapsed: small pulse dot
  if (collapsed) {
    return (
      <div
        data-testid="mini-orb-pill"
        onMouseDown={startDrag}
        onTouchStart={startDrag}
        onClick={() => { if (!movedRef.current) setCollapsed(false); }}
        style={{
          position: "fixed", left: pos.x, top: pos.y, zIndex: 95,
          width: 24, height: 24, borderRadius: "50%",
          background: `radial-gradient(circle, ${colour} 0%, transparent 70%)`,
          boxShadow: `0 0 16px ${colour}`,
          cursor: "grab", touchAction: "none",
          animation: "mini-orb-pulse 2s ease-in-out infinite",
        }}
        title="Tap to expand mini orb"
      />
    );
  }

  return (
    <div
      data-testid="mini-orb-widget"
      style={{
        position: "fixed", left: pos.x, top: pos.y, zIndex: 95,
        width: 120, height: 120,
        display: "flex", alignItems: "center", justifyContent: "center",
        touchAction: "none",
      }}
    >
      {/* Tiny header — drag bar + collapse pip */}
      <div
        onMouseDown={startDrag}
        onTouchStart={startDrag}
        style={{
          position: "absolute", top: -22, left: 0, right: 0,
          display: "flex", alignItems: "center", justifyContent: "space-between",
          fontFamily: "'Share Tech Mono', monospace", fontSize: 8, letterSpacing: 1,
          color: "rgba(255,184,0,0.65)", cursor: "grab", userSelect: "none",
          padding: "2px 6px",
        }}
      >
        <span>// ORB · DRAG</span>
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); setCollapsed(true); }}
          onMouseDown={(e) => e.stopPropagation()}
          onTouchStart={(e) => e.stopPropagation()}
          style={{
            background: "transparent", border: "none", color: "var(--rc-gold)",
            cursor: "pointer", fontSize: 12, padding: 0, lineHeight: 1,
          }}
          title="Collapse to dot"
          data-testid="mini-orb-collapse"
        >─</button>
      </div>

      {/* The orb itself - radial gradient with breathing pulse */}
      <div
        onClick={handleTap}
        onMouseDown={startDrag}
        onTouchStart={startDrag}
        data-testid="mini-orb-tap"
        style={{
          width: 96, height: 96, borderRadius: "50%",
          background: `radial-gradient(circle at 30% 30%,
            ${active || speaking ? colour : `${colour}cc`} 0%,
            ${colour}55 35%,
            transparent 75%)`,
          boxShadow: `
            0 0 ${active || speaking ? 36 : 20}px ${colour},
            inset 0 0 20px ${colour}55
          `,
          cursor: "pointer",
          animation: speaking
            ? "mini-orb-speak 0.6s ease-in-out infinite"
            : active
              ? "mini-orb-listen 1.2s ease-in-out infinite"
              : "mini-orb-pulse 3s ease-in-out infinite",
          transition: "box-shadow 0.3s ease",
        }}
        title="Tap to talk"
      />

      {/* Status label */}
      <div style={{
        position: "absolute", bottom: -16, left: 0, right: 0, textAlign: "center",
        fontFamily: "'Share Tech Mono', monospace", fontSize: 8, letterSpacing: 2,
        color: speaking ? "#7CFFB7" : active ? "var(--rc-gold)" : "rgba(255,255,255,0.45)",
        pointerEvents: "none",
      }}>
        {speaking ? "SPEAKING" : active ? "LISTENING" : "TAP TO TALK"}
      </div>

      {/* Keyframes - inline so the component is self-contained */}
      <style>{`
        @keyframes mini-orb-pulse {
          0%, 100% { transform: scale(1);   opacity: 0.88; }
          50%      { transform: scale(1.05); opacity: 1; }
        }
        @keyframes mini-orb-listen {
          0%, 100% { transform: scale(1);    box-shadow: 0 0 24px ${colour}, inset 0 0 22px ${colour}66; }
          50%      { transform: scale(1.08); box-shadow: 0 0 48px ${colour}, inset 0 0 28px ${colour}99; }
        }
        @keyframes mini-orb-speak {
          0%, 100% { transform: scale(1);    }
          50%      { transform: scale(1.12); }
        }
      `}</style>
    </div>
  );
}
